<template>
  <div class="navbar-container d-flex content align-items-center justify-content-between w-100">
    <!-- Left: Brand Logo & Top Rush Navigation Tabs -->
    <div class="navbar-left-section d-flex align-items-center">
      <!-- App Brand Logo -->
      <router-link
        :to="{ name: 'popular-users' }"
        class="navbar-brand-link d-flex align-items-center"
      >
        <b-img
          v-if="appLogoImage"
          :src="appLogoImage"
          alt="Boho"
          class="navbar-logo-img"
        />
        <span v-else class="navbar-logo-fallback">BOHO</span>
      </router-link>

      <!-- Rush Style Navigation Tabs -->
      <nav class="rush-nav-tabs d-flex align-items-center">
        <!-- Keşfet / Explore -->
        <router-link
          :to="{ name: 'moments', query: { tab: 'discoverygrid' } }"
          class="nav-tab-item"
          :class="{ active: isExploreActive }"
        >
          <svg class="tab-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"></polygon>
          </svg>
          <span class="tab-label">{{ $t('Explore') || 'Keşfet' }}</span>
        </router-link>

        <!-- Sana Özel / For You -->
        <router-link
          :to="{ name: 'moments', query: { tab: 'foryou' } }"
          class="nav-tab-item"
          :class="{ active: isForYouActive }"
        >
          <svg class="tab-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
          <span class="tab-label">{{ $t('For You') || 'Sana Özel' }}</span>
        </router-link>

        <!-- Moments / Feed (Active pink/purple underline bar) -->
        <router-link
          :to="{ name: 'moments', query: { tab: 'discover' } }"
          class="nav-tab-item moments-tab"
          :class="{ active: isMomentsActive }"
        >
          <span class="moments-play-badge">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
          </span>
          <span class="tab-label">Moments</span>
        </router-link>

        <!-- Sohbet / Chats -->
        <router-link
          :to="{ name: 'chat' }"
          class="nav-tab-item"
          :class="{ active: $route.name === 'chat' }"
        >
          <svg class="tab-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
          <span class="tab-label">{{ $t('Chats') || 'Chats' }}</span>
          <span v-if="unreadCount > 0" class="tab-badge">{{ unreadCount }}</span>
        </router-link>
      </nav>
    </div>

    <!-- Right: Market Gold Pill, Notification & User Dropdown -->
    <div class="navbar-right-section d-flex align-items-center ml-auto" v-if="user">
      <!-- Market Golden Pill Button with Shimmer -->
      <router-link :to="{ name: 'market' }" class="nav-market-gold-btn">
        <b-img
          :src="require('@/assets/images/icons/diamonds-1.svg')"
          width="18"
          height="15"
          alt="diamonds"
          class="market-diamond-icon"
        />
        <span class="market-label">{{ $t('Market') || 'Market' }}</span>
      </router-link>

      <!-- Beğendiklerim (takip ettiğim kullanıcılar) -->
      <router-link
        :to="{ name: 'interaction.likes' }"
        class="nav-liked-btn"
        :class="{ active: $route.name === 'interaction.likes' }"
        :title="$t('Users I like')"
        :aria-label="$t('Users I like')"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
      </router-link>

      <!-- Notifications Dropdown (Dark Circle with Bell & Pink Dot) -->
      <notification-dropdown />

      <!-- User Dropdown (Avatar + Name + Chevron) -->
      <user-dropdown />
    </div>
  </div>
</template>

<script>
import {
  BImg,
} from 'bootstrap-vue'
import { $themeConfig } from '@themeConfig'
import NotificationDropdown from './components/NotificationDropdown.vue'
import UserDropdown from './components/UserDropdown.vue'

export default {
  components: {
    BImg,
    NotificationDropdown,
    UserDropdown,
  },
  props: {
    toggleVerticalMenuActive: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      appLogoImage: $themeConfig.app.appLogoImage,
    }
  },
  computed: {
    user() {
      return this.$store.getters['auth/user']
    },
    unreadCount() {
      return this.$store.getters['socket/unreadChatCount'] || 0
    },
    isExploreActive() {
      const exploreRoutes = ['popular-users', 'index', 'discover', 'search-users', 'new-users', 'near-by', 'online-users', 'video-call-users']
      return (this.$route.name === 'moments' && (this.$route.query.tab === 'discoverygrid' || !this.$route.query.tab)) || (exploreRoutes.includes(this.$route.name) && this.$route.name !== 'moments')
    },
    isForYouActive() {
      return this.$route.name === 'for-you' || (this.$route.name === 'moments' && this.$route.query.tab === 'foryou')
    },
    isMomentsActive() {
      return this.$route.name === 'moments' && this.$route.query.tab === 'discover'
    },
  },
}
</script>

<style lang="scss" scoped>
.navbar-container {
  min-height: 52px;
}

.navbar-left-section {
  display: flex;
  align-items: center;
  gap: 18px;
  overflow: visible;

  @media (max-width: 991px) {
    gap: 8px;
  }
}

/* Brand Logo */
.navbar-brand-link {
  text-decoration: none;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  overflow: visible;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.92;
  }

  /* logo.png 1024x1024 kare ve üst/alt ~%19 şeffaf boşluk var.
     Kutu oranını ~1.54:1 yapıp object-fit: cover ile boşluğu kırpıyoruz;
     transform: scale kullanmıyoruz (yerleşimi bozuyordu). */
  .navbar-logo-img {
    height: 58px;
    width: 89px;
    max-height: none;
    object-fit: cover;
    object-position: center;
    display: block;
    transform: none;
    margin: 0;
    transition: all 0.2s ease;

    @media (max-width: 1199px) {
      height: 52px !important;
      width: 80px !important;
    }

    @media (max-width: 991px) {
      height: 46px !important;
      width: 71px !important;
    }

    /* Mobilde üst sekmeler gizli; logo için yer var */
    @media (max-width: 767px) {
      height: 52px !important;
      width: 80px !important;
    }

    @media (max-width: 576px) {
      height: 48px !important;
      width: 74px !important;
    }
  }

  .navbar-logo-fallback {
    font-size: 26px;
    font-weight: 900;
    letter-spacing: 1.5px;
    background: linear-gradient(135deg, #7c3aed, #ea03b2);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    transition: all 0.2s ease;
    margin-right: 0;

    @media (max-width: 1199px) {
      font-size: 22px !important;
    }

    @media (max-width: 768px) {
      font-size: 19px !important;
    }

    @media (max-width: 576px) {
      font-size: 22px !important;
    }
  }
}

/* Navigation Tabs */
.rush-nav-tabs {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 0;

  @media (max-width: 1199px) {
    gap: 3px;
  }

  @media (max-width: 991px) {
    gap: 2px;

    .nav-tab-item {
      padding: 5px 8px !important;

      .tab-label {
        display: none;
      }

      &.moments-tab {
        padding: 5px 8px !important;
      }
    }
  }

  @media (max-width: 767px) {
    display: none !important;
  }

  .nav-tab-item {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 7px 16px;
    border-radius: 9999px;
    border: 1.5px solid transparent;
    text-decoration: none;
    color: #94a3b8;
    font-size: 13.5px;
    font-weight: 500;
    transition: all 0.22s ease;
    white-space: nowrap;
    position: relative;

    @media (max-width: 1199px) {
      padding: 6px 12px;
      font-size: 13px;
      gap: 5px;
    }

    .tab-icon {
      stroke: #94a3b8;
      stroke-width: 1.8;
      transition: all 0.2s ease;
    }

    &:hover:not(.active) {
      color: #ffffff;
      background: rgba(255, 255, 255, 0.07);
      border-color: rgba(255, 255, 255, 0.12);

      .tab-icon {
        stroke: #ffffff;
      }
    }

    &.active {
      color: #ffffff;
      font-weight: 700; /* Bold for selected tab */
      background: linear-gradient(135deg, rgba(234, 3, 178, 0.28) 0%, rgba(168, 85, 247, 0.22) 50%, rgba(99, 102, 241, 0.12) 100%);
      border: 1px solid rgba(234, 3, 178, 0.35);
      border-radius: 9999px;
      box-shadow: 0 2px 14px rgba(234, 3, 178, 0.18), inset 0 1px 1px rgba(255, 255, 255, 0.25), inset 0 0 12px rgba(234, 3, 178, 0.12);
      backdrop-filter: blur(12px);

      .tab-icon {
        stroke: #ffffff;
        filter: drop-shadow(0 0 4px rgba(234, 3, 178, 0.55));
        stroke-width: 2.2;
      }

      .tab-label {
        color: #ffffff;
        text-shadow: 0 0 8px rgba(234, 3, 178, 0.35);
      }
    }

    &.moments-tab {
      color: #cbd5e1;
      font-weight: 500;

      .moments-play-badge {
        background: #ea03b2;
        color: #ffffff;
        width: 18px;
        height: 15px;
        border-radius: 4px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        svg {
          width: 8px;
          height: 8px;
        }
      }

      &.active {
        color: #ffffff;
        font-weight: 700;

        .moments-play-badge {
          box-shadow: 0 0 6px rgba(234, 3, 178, 0.5);
        }
      }

      &:hover:not(.active) {
        color: #ffffff;
      }
    }

    .tab-badge {
      background: #ea03b2;
      color: #ffffff;
      width: 17px;
      height: 17px;
      border-radius: 50%;
      font-size: 10px;
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      margin-left: 2px;
    }
  }
}

/* Right Section Items */
/* Beğendiklerim butonu (zil butonuyla aynı görünüm) */
.nav-liked-btn {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #ffffff;
  transition: all 0.2s ease;

  &:hover,
  &.active {
    color: #f472b6;
    background: rgba(244, 114, 182, 0.16);
  }

  &.active svg {
    fill: currentColor;
  }

  @media (max-width: 576px) {
    width: 34px;
    height: 34px;

    svg {
      width: 16px;
      height: 16px;
    }
  }
}

.navbar-right-section {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 991px) {
    gap: 6px;
  }

  @media (max-width: 576px) {
    gap: 4px;
  }
}

/* Market Golden Pill Button with Shimmer Animation */
.nav-market-gold-btn {
  font-family: inherit;
  font-weight: 700;
  padding: 6px 14px;
  border-radius: 9999px;
  font-size: 13px;
  color: #ffffff !important;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 2px 10px rgba(245, 158, 11, 0.4);
  transition: all 0.25s ease;
  white-space: nowrap;
  position: relative;
  overflow: hidden;

  .market-label {
    color: #ffffff !important;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
  }

  .market-diamond-icon {
    object-fit: contain;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.25));
    transition: transform 0.2s ease;
  }

  /* Shimmer Light Beam Effect */
  &::after {
    content: '';
    position: absolute;
    top: -50%;
    left: -75%;
    width: 60%;
    height: 200%;
    background: linear-gradient(
      60deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.6) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    transform: rotate(25deg);
    animation: goldShimmer 2.8s infinite;
    pointer-events: none;
  }

  &:hover {
    background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
    transform: translateY(-1px);
    color: #ffffff !important;
    box-shadow: 0 4px 16px rgba(245, 158, 11, 0.6);

    .market-label {
      color: #ffffff !important;
    }

    .market-diamond-icon {
      transform: scale(1.1);
    }
  }

  @media (max-width: 1199px) {
    padding: 6px 14px;
    font-size: 12.5px;
  }

  @media (max-width: 576px) {
    padding: 5px 10px;
    font-size: 12px;
    gap: 5px;

    /* Mobilde de "Market" yazısı görünsün */
    .market-label {
      display: inline;
    }
  }
}

@keyframes goldShimmer {
  0% {
    left: -80%;
  }
  30% {
    left: 140%;
  }
  100% {
    left: 140%;
  }
}

::v-deep .nav-item {
  list-style: none;
  margin: 0 !important;
  padding: 0 !important;

  .nav-link {
    color: #ffffff !important;
    padding: 0 !important;
    margin: 0 !important;

    &:hover {
      color: #ea03b2 !important;
    }
  }
}
</style>
